import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { PartialRecord, StorageLevel } from '@twaice-fe/shared/models';
import { firstValueFrom, Observable, of, take } from 'rxjs';
import { CustomerNameTranslationService } from './customer-name-translation.service';
import { SystemsService } from './systems.service';
import { LevelNameTranslationPipe } from './level-name-translation.pipe';

const { systemSelectors } = selectors;

export enum DetailsDrawer {
  Imbalance = 'Imbalance',
  WeakCell = 'WeakCell',
  LimitingCell = 'LimitingCell',
  NonOperatingString = 'NonOperatingString',
  NonOperatingPcsModule = 'NonOperatingPcsModule',
  SoCError = 'SoCError',
  BalanceOrTakeOffline = 'BalanceOrTakeOffline',
  ManualInterventionOrTakeOffline = 'ManualInterventionOrTakeOffline',
  ReplaceOrTakeOffline = 'ReplaceOrTakeOffline',
}

const causesWithDeepLinks = [
  DetailsDrawer.Imbalance,
  DetailsDrawer.WeakCell,
  DetailsDrawer.LimitingCell,
  DetailsDrawer.NonOperatingString,
  DetailsDrawer.NonOperatingPcsModule,
  DetailsDrawer.BalanceOrTakeOffline,
  DetailsDrawer.ManualInterventionOrTakeOffline,
  DetailsDrawer.ReplaceOrTakeOffline,
];

const deepLinkTextMap: PartialRecord<DetailsDrawer, string> = {
  [DetailsDrawer.Imbalance]: 'View in SoC Spreads',
  [DetailsDrawer.WeakCell]: 'View in Data Explorer',
  [DetailsDrawer.LimitingCell]: 'View in Data Explorer',
  [DetailsDrawer.NonOperatingString]: 'View in Smart Alerts',
  [DetailsDrawer.NonOperatingPcsModule]: 'View in Data Explorer',
  [DetailsDrawer.BalanceOrTakeOffline]: 'View in SoC Spreads',
  [DetailsDrawer.ManualInterventionOrTakeOffline]: 'View in Data Explorer',
  [DetailsDrawer.ReplaceOrTakeOffline]: 'View in Data Explorer',
};

type DeepLinkNavigationMap = {
  [K in keyof typeof deepLinkTextMap]: () => Promise<boolean> | Promise<void> | void;
};

@Injectable()
export class DetailsDrawerService {
  constructor(
    protected store: Store,
    protected router: Router,
    protected route: ActivatedRoute,
    protected systemsService: SystemsService,
    protected customerNameTranslationService: CustomerNameTranslationService,
    private levelIdentifierToLevelName: LevelNameTranslationPipe
  ) {}

  hasDeepLink(cause: DetailsDrawer | null) {
    return cause ? causesWithDeepLinks.includes(cause) : false;
  }

  getDrawerTitle(componentBk: string, cause: DetailsDrawer | null): string {
    if (!cause) return '';

    const titleTextMap = {
      [DetailsDrawer.Imbalance]: 'Balancing Recommended',
      [DetailsDrawer.WeakCell]: `Weak ${this.levelIdentifierToLevelName.transform(StorageLevel.Cell.toUpperCase())} Identified`,
      [DetailsDrawer.LimitingCell]: `Limiting ${this.levelIdentifierToLevelName.transform(
        StorageLevel.Cell.toUpperCase()
      )} Identified`,
      [DetailsDrawer.NonOperatingString]: `Non-Operating ${this.levelIdentifierToLevelName.transform(
        StorageLevel.String.toUpperCase()
      )} Detected`,
      [DetailsDrawer.NonOperatingPcsModule]: `Non-Operating ${this.levelIdentifierToLevelName.transform(
        StorageLevel.InverterModule.toUpperCase()
      )} Detected`,
      [DetailsDrawer.SoCError]: 'SoC Error',
      [DetailsDrawer.BalanceOrTakeOffline]: 'Take Offline or Balance',
      [DetailsDrawer.ManualInterventionOrTakeOffline]: 'Take Offline or Manual Intervention',
      [DetailsDrawer.ReplaceOrTakeOffline]: 'Take Offline or Replace',
    };

    return `${this.customerNameTranslationService.translateComponentBk(componentBk)}: ${titleTextMap[cause]}`;
  }

  async navigateToDeepLink(
    stringBk: string | undefined,
    date: string | null,
    cause: DetailsDrawer | null,
    inverterBk: string | null,
    pcsModuleBks: string[]
  ) {
    if (!this.hasDeepLink(cause)) return;

    const navigateToSoCSpreads = () => {
      const qparams = this.route.snapshot.queryParams;
      const queryParams = {
        ...(qparams['startDate'] && qparams['endDate']
          ? {
              startDate: qparams['startDate'],
              endDate: qparams['endDate'],
              timeRange: 'custom',
            }
          : {}),
        ...(inverterBk
          ? {
              componentBk: inverterBk,
            }
          : {}),
      };
      // eslint-disable-next-line twaice/no-router-navigate-outside-effects
      return this.router.navigate(['../', 'balancing'], {
        relativeTo: this.route,
        queryParams,
      });
    };

    const deepLinkNavigationMap: DeepLinkNavigationMap = {
      [DetailsDrawer.Imbalance]: navigateToSoCSpreads,
      [DetailsDrawer.WeakCell]: () => this.navigateToWeakCellDataExplorer(stringBk ?? '', date),
      [DetailsDrawer.LimitingCell]: () => this.navigateToLimitingCellDataExplorer(stringBk ?? '', date),
      [DetailsDrawer.NonOperatingString]: () => this.navigateToSmartAlerts(),
      [DetailsDrawer.NonOperatingPcsModule]: () => this.navigateToPcsModuleDataExplorer(pcsModuleBks, date),
      [DetailsDrawer.BalanceOrTakeOffline]: navigateToSoCSpreads,
      [DetailsDrawer.ManualInterventionOrTakeOffline]: () => this.navigateToLimitingCellDataExplorer(stringBk ?? '', date),
      [DetailsDrawer.ReplaceOrTakeOffline]: () => this.navigateToWeakCellDataExplorer(stringBk ?? '', date),
    };

    return deepLinkNavigationMap[cause as DetailsDrawer]?.();
  }

  getDeepLinkText(cause: DetailsDrawer | null): Observable<string | null> {
    return of(cause ? deepLinkTextMap[cause] ?? null : null);
  }

  private navigateToSmartAlerts() {
    const queryParams = this.route.snapshot.queryParams;
    const { selectedAvailabilityDate, startDate, endDate, timeRange } = queryParams;

    // eslint-disable-next-line twaice/no-router-navigate-outside-effects
    this.router.navigate(['/alerts/rule-evaluations'], {
      queryParams: {
        startDate,
        endDate,
        timeRange,
        ...(selectedAvailabilityDate && {
          selectedDate: new Date(selectedAvailabilityDate).getTime(),
        }),
      },
    });
  }

  private navigateToDataExplorer(sensorBks: string[], componentBks: string[], availabilityDate: string | null | undefined) {
    const selectedDate = availabilityDate ? new Date(availabilityDate) : new Date();
    const selectedDateTime = selectedDate.getTime();
    const baseUrl = 'data-explorer';
    const sensorParams = {
      componentBk: componentBks,
      sk: sensorBks,
    };
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['../../', baseUrl], {
        relativeTo: this.route,
        queryParams: {
          ...sensorParams,
          timeRes: 60 * 1000,
          startDate: selectedDateTime,
          endDate: selectedDateTime + 24 * 60 * 60 * 1000,
          timeRange: 'custom',
        },
      })
    );
    window.open(url, '_blank');
  }

  private async navigateToPcsModuleDataExplorer(
    pcsModuleBks: string[],
    availabilityDate: string | null | undefined
  ): Promise<void> {
    const system = await firstValueFrom(this.store.select(systemSelectors.getSelected).pipe(take(1)));
    if (system === undefined) {
      // eslint-disable-next-line twaice/no-router-navigate-outside-effects
      this.router.navigate(['../../', 'data-explorer'], { relativeTo: this.route });
      return;
    }

    const systemBk = system.systemBk;
    const customerBk = system.customerBk;
    const sensorType = 'voltage';
    const sensorSubType = 'DC';

    const sensorArrays = await Promise.all(
      pcsModuleBks.map((componentBk) =>
        firstValueFrom(
          this.systemsService.getComponentSensorSpecs({ customerBk, systemBk, componentBk, sensorType, sensorSubType })
        )
      )
    );
    const sensors = sensorArrays.flat();
    const sensorKeys = sensors.map((sensor) => sensor.key);

    this.navigateToDataExplorer(sensorKeys, pcsModuleBks, availabilityDate);
  }

  private async navigateToWeakCellDataExplorer(stringBk: string, availabilityDate: string | null | undefined): Promise<void> {
    const system = await firstValueFrom(this.store.select(systemSelectors.getSelected).pipe(take(1)));
    if (system === undefined) {
      // eslint-disable-next-line twaice/no-router-navigate-outside-effects
      this.router.navigate(['../../', 'data-explorer'], { relativeTo: this.route });
      return;
    }

    const systemBk = system.systemBk;
    const customerBk = system.customerBk;
    const sensorType = 'voltage';
    const sensorSubType = 'none';
    const aggregationTypeMax = 'max';
    const aggregationTypeMin = 'max';
    const aggregationLevel = 'cell';

    const sensorsMax = await firstValueFrom(
      this.systemsService.getComponentSensorSpecs({
        systemBk,
        customerBk,
        componentBk: stringBk,
        sensorType,
        sensorSubType,
        aggregationType: aggregationTypeMax,
        aggregationLevel,
      })
    );
    const sensorsMin = await firstValueFrom(
      this.systemsService.getComponentSensorSpecs({
        systemBk,
        customerBk,
        componentBk: stringBk,
        sensorType,
        sensorSubType,
        aggregationType: aggregationTypeMin,
        aggregationLevel,
      })
    );
    const sensors = sensorsMax.concat(sensorsMin);
    const sensorKeys = sensors.map((sensor) => sensor.key);

    this.navigateToDataExplorer(sensorKeys, [stringBk], availabilityDate);
  }

  private async navigateToLimitingCellDataExplorer(stringBk: string, lastDetectedDate: string | null | undefined): Promise<void> {
    const system = await firstValueFrom(this.store.select(systemSelectors.getSelected).pipe(take(1)));
    if (system === undefined) {
      // eslint-disable-next-line twaice/no-router-navigate-outside-effects
      this.router.navigate(['../../', 'data-explorer'], { relativeTo: this.route });
      return;
    }

    const systemBk = system.systemBk;
    const customerBk = system.customerBk;
    const sensorSpecs = [
      {
        sensorType: 'voltage',
        sensorSubType: 'none',
        aggregationType: 'min',
        aggregationLevel: 'cell',
      },
      {
        sensorType: 'voltage',
        sensorSubType: 'none',
        aggregationType: 'max',
        aggregationLevel: 'cell',
      },
      {
        sensorType: 'voltage',
        sensorSubType: 'cell',
        aggregationType: 'min',
        aggregationLevel: 'cell',
        valueType: 'position',
      },
      {
        sensorType: 'voltage',
        sensorSubType: 'module',
        aggregationType: 'min',
        aggregationLevel: 'cell',
        valueType: 'position',
      },
      {
        sensorType: 'soc',
        sensorSubType: 'none',
        aggregationType: 'none',
        aggregationLevel: 'none',
        valueType: 'value',
      },
    ];

    const sensorArrays = await Promise.all(
      sensorSpecs.map((sensorSpec) =>
        firstValueFrom(
          this.systemsService.getComponentSensorSpecs({
            systemBk,
            customerBk,
            componentBk: stringBk,
            ...sensorSpec,
          })
        )
      )
    );
    const sensorKeys = [...new Set(sensorArrays.flat().map((sensor) => sensor.key))];

    this.navigateToDataExplorer(sensorKeys, [stringBk], lastDetectedDate);
  }
}
